import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { resumoDaFormatacao, temTimbrado, timbradoDoArquivo, type ExtracaoDeArquivo } from '../formatacao';
import type { Margens, Timbrado } from '../documento';
import type { AlcanceDoModelo } from '../estilos';
import { PainelDialogo, enterAplica, rodapeModal } from './paineis';
import { PreviaDaFolha } from './editorDeTimbrado';
import { BotaoNu } from './comum';

const ACEITA = '.docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * Formatação do texto: a fonte, o parágrafo, a página e o timbrado de uma
 * petição do advogado, nesta peça ou como modelo. Quem mede o arquivo é o app
 * (`extrair`); o painel mostra o que foi medido, ao lado da folha como fica, e
 * só aplica isso. No formato do painel do timbrado: escolhas à esquerda, a
 * folha à direita (embaixo, no celular).
 */
export function PainelFormatacao({ aberto, extrair, alcances, escritorioAjustaOCorpo = false, doEscritorio = false, timbrado, timbradoAtual, margens, numerada, aoFechar, aoAplicar }: {
  aberto: boolean;
  /** Rejeita com a mensagem que o painel mostra (o app a escreve). */
  extrair: (arquivo: File) => Promise<ExtracaoDeArquivo>;
  /**
   * Onde o "como modelo" guarda o Corpo da peça: o seu (`pessoal`) e/ou o de
   * todo o escritório (proprietário ou sócio). Só `escritorio` é a conta de quem
   * trabalha sem escritório. Vazio: só nesta peça.
   */
  alcances: AlcanceDoModelo[];
  /** O escritório já ajustou o Corpo da peça: onde os dois ajustam, vale o dele. */
  escritorioAjustaOCorpo?: boolean;
  /**
   * Fora de uma peça: a aba Peças do escritório importa de uma petição o padrão dele
   * (timbrado, página e Corpo). Sem "Onde vale"; aplicar é sempre "como modelo".
   */
  doEscritorio?: boolean;
  /**
   * O cabeçalho e o rodapé do arquivo podem virar o timbrado da pessoa
   * (`proprio`); com o do escritório por cima, não (`escritorio`); `null` na
   * conta de outra pessoa.
   */
  timbrado: 'proprio' | 'escritorio' | null;
  /** O timbrado em uso na peça e a página dela: a folha em miniatura parte deles. */
  timbradoAtual: Timbrado | null;
  margens: Margens;
  numerada: boolean;
  aoFechar: () => void;
  aoAplicar: (x: ExtracaoDeArquivo, opcoes: { comoModelo: AlcanceDoModelo | null; usarTimbrado: boolean }) => void;
}) {
  const [lido, setLido] = useState<{ nome: string; x: ExtracaoDeArquivo } | null>(null);
  const [lendo, setLendo] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [comoModelo, setComoModelo] = useState<AlcanceDoModelo | null>(null);
  const [usarTimbrado, setUsarTimbrado] = useState(true);
  // O texto do cabeçalho e do rodapé lido do arquivo, que a pessoa pode corrigir antes de usar.
  const [textos, setTextos] = useState({ cabecalho: '', rodape: '' });
  // Cada leitura tem o seu número; abrir ou fechar o painel muda o número, e a
  // resposta de uma leitura antiga (o painel foi fechado no meio) não entra.
  const leitura = useRef(0);
  useEffect(() => {
    leitura.current += 1;
    if (!aberto) return;
    setLido(null);
    setLendo(null);
    setErro(null);
    setComoModelo(null);
    setUsarTimbrado(true);
    setTextos({ cabecalho: '', rodape: '' });
  }, [aberto]);

  const carregar = async (e: ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0];
    e.target.value = '';
    if (!arquivo) return;
    const esta = ++leitura.current;
    // O arquivo anterior deixa de valer: "Aplicar" no meio da troca não o aplica.
    setLido(null);
    setLendo(arquivo.name);
    setErro(null);
    try {
      const x = await extrair(arquivo);
      if (esta !== leitura.current) return;
      if (resumoDaFormatacao(x).length || temTimbrado(x)) {
        setLido({ nome: arquivo.name, x });
        setTextos({ cabecalho: x.timbrado?.cabecalho ?? '', rodape: x.timbrado?.rodape ?? '' });
      }
      else {
        setLido(null);
        setErro(`Não consegui medir a formatação de “${arquivo.name}”. Se for um PDF digitalizado, envie o Word.`);
      }
    } catch (err) {
      if (esta !== leitura.current) return;
      setErro(err instanceof Error && err.message ? err.message : `Não consegui ler “${arquivo.name}”.`);
    } finally {
      if (esta === leitura.current) setLendo(null);
    }
  };
  // O lido com os textos que a pessoa editou: é o que a folha mostra e o que se aplica.
  const editado: ExtracaoDeArquivo | null = lido && (lido.x.timbrado ? { ...lido.x, timbrado: { ...lido.x.timbrado, ...textos } } : lido.x);
  const comTimbrado = !!lido && temTimbrado(lido.x);
  const aplicar = () => {
    if (editado) aoAplicar(editado, { comoModelo: doEscritorio ? 'escritorio' : comoModelo && alcances.includes(comoModelo) ? comoModelo : null, usarTimbrado: comTimbrado && timbrado === 'proprio' && usarTimbrado });
  };
  // Enter no seletor de arquivo abre o seletor; nos demais lugares, aplica.
  const teclas = (e: KeyboardEvent) => { if ((e.target as HTMLInputElement).type !== 'file') enterAplica(aplicar)(e); };
  const linhas = lido ? resumoDaFormatacao(lido.x) : [];
  const usaDoArquivo = comTimbrado && timbrado === 'proprio' && usarTimbrado;
  const previa = (usaDoArquivo && timbradoDoArquivo(editado!, timbradoAtual, 'arquivo')) || timbradoAtual;
  const faixas = lido?.x.timbrado;
  const campoDoTimbrado = (chave: 'cabecalho' | 'rodape', rotulo: string, linhas: number) =>
    (chave === 'cabecalho' ? faixas?.imagemCabecalho : faixas?.imagemRodape) ? (
      <p className="cm-ef-mini-leg">{rotulo}: a imagem do arquivo, como na folha ao lado.</p>
    ) : (
      <label className="cm-ef-rot">{rotulo}{chave === 'cabecalho' ? ' (a primeira linha sai em negrito)' : ''}
        <textarea className="cm-ef-campo cm-ef-campo-area" rows={linhas} value={textos[chave]}
          onChange={(e) => setTextos((t) => ({ ...t, [chave]: e.target.value }))} />
      </label>
    );
  const f = lido?.x.formato;
  // Só "todo o escritório", sem "só para mim": a conta de quem trabalha sem escritório.
  const semEscritorio = !alcances.includes('pessoal');
  const PAGINA = 'as margens e o papel, a página com que as suas próximas peças abrem';
  const legendaDoAlcance = comoModelo === 'pessoal'
    ? `A fonte e o parágrafo passam a ser o seu “Corpo da peça”, nesta e nas suas próximas peças; ${PAGINA}.${escritorioAjustaOCorpo ? ' Onde o escritório já ajustou o Corpo da peça, vale o dele.' : ''}`
    : comoModelo === 'escritorio'
      ? `A fonte e o parágrafo passam a ser o “Corpo da peça”${semEscritorio ? '' : ' de todo o escritório'}, nesta e nas próximas peças; ${PAGINA}.`
      : 'Muda os parágrafos no “Corpo da peça” e as margens só desta peça.';

  return (
    <PainelDialogo
      aberto={aberto}
      aoFechar={aoFechar}
      titulo="Formatação do texto"
      descricao={doEscritorio
        ? 'A fonte, o parágrafo, a página e o timbrado de uma petição, como o padrão das peças do escritório.'
        : 'A fonte, o parágrafo e as margens de uma petição sua, aplicados a esta peça.'}
      rodape={rodapeModal(aplicar, aoFechar, doEscritorio ? 'Usar como padrão do escritório' : comoModelo ? 'Aplicar e usar como modelo' : 'Aplicar nesta peça', !lido)}
    >
      <div className="cm-ef-painel" onKeyDown={teclas}>
      <div className="cm-ef-painel-campos">
        <section className="cm-ef-secao">
          <h3>Petição de referência</h3>
          <label className="cm-ef-arquivo" aria-disabled={!!lendo || undefined}>
            {lendo ? <><Spinner size={16} className="text-inherit" />Lendo {lendo}…</>
              : lido ? `Trocar o arquivo (${lido.nome})` : 'Escolher um arquivo (Word ou PDF)'}
            {/* `sr-only`, não `display: none`: o seletor recebe o foco pelo Tab. */}
            <input type="file" className="sr-only" accept={ACEITA} disabled={!!lendo} onChange={(e) => void carregar(e)} />
          </label>
          <p className="cm-ef-mini-leg">Só a forma e o timbrado são lidos: o texto do arquivo não entra na peça, e o arquivo não fica guardado.</p>
          {erro && <p className="cm-ef-erro-campo" role="alert">{erro}</p>}
        </section>
        {lido && linhas.length > 0 && (
          <section className="cm-ef-secao">
            <h3>O que foi medido</h3>
            <dl className="cm-ef-medidas">
              {linhas.map((l) => {
                const [rotulo, valor] = l.split(': ', 2);
                return <div key={l}><dt>{rotulo}</dt><dd>{valor}</dd></div>;
              })}
            </dl>
            {lido.x.avisos.map((a) => <p key={a} className="cm-ef-mini-leg">{a}</p>)}
          </section>
        )}
        {comTimbrado && (
          <section className="cm-ef-secao">
            <h3>Papel timbrado</h3>
            {timbrado === 'proprio' && (
              <label className="cm-ef-check">
                <input type="checkbox" checked={usarTimbrado} onChange={(e) => setUsarTimbrado(e.target.checked)} />
                {doEscritorio ? 'Usar o papel deste arquivo como timbrado do escritório' : 'Usar o papel deste arquivo como meu timbrado'}
              </label>
            )}
            {timbrado === 'proprio' && usarTimbrado && (
              <>
                {campoDoTimbrado('cabecalho', 'Cabeçalho', 3)}
                {campoDoTimbrado('rodape', 'Rodapé', 2)}
                <p className="cm-ef-mini-leg">{doEscritorio
                  ? 'Vale nas peças de quem segue o padrão do escritório, e sai no PDF e no Word.'
                  : 'Vale nesta peça e nas próximas, e sai no PDF e no Word.'}</p>
              </>
            )}
            {timbrado === null && (
              <p className="cm-ef-mini-leg">Na conta de outra pessoa o timbrado é o dela: o deste arquivo não entra.</p>
            )}
            {timbrado === 'escritorio' && (
              <p className="cm-ef-mini-leg">O timbrado do escritório é o que vale nas suas peças: o deste arquivo não entra.</p>
            )}
          </section>
        )}
        {lido && !doEscritorio && alcances.length > 0 && (
          <section className="cm-ef-secao">
            <h3>Onde vale</h3>
            <div className="cm-ef-seg cm-ef-seg-txt" role="group" aria-label="Onde vale">
              <BotaoNu aria-pressed={!comoModelo} onClick={() => setComoModelo(null)}>Só nesta peça</BotaoNu>
              {alcances.includes('pessoal') && (
                <BotaoNu aria-pressed={comoModelo === 'pessoal'} onClick={() => setComoModelo('pessoal')}>Só para mim</BotaoNu>
              )}
              {alcances.includes('escritorio') && (
                <BotaoNu aria-pressed={comoModelo === 'escritorio'} onClick={() => setComoModelo('escritorio')}>
                  {semEscritorio ? 'Como modelo' : 'Todo o escritório'}
                </BotaoNu>
              )}
            </div>
            <p className="cm-ef-mini-leg">{legendaDoAlcance}</p>
          </section>
        )}
      </div>
      <aside className="cm-ef-diagrama" aria-label="Como fica">
        <h3>Como fica</h3>
        <PreviaDaFolha
          t={previa}
          numerada={numerada || !!lido?.x.timbrado?.numeroDePagina}
          margens={{ ...margens, ...lido?.x.documento.margens }}
          recuo={f?.especial === 'primeira' ? (f.medida ?? 0) : 0}
        />
      </aside>
      </div>
    </PainelDialogo>
  );
}
